import { skipToken } from "@reduxjs/toolkit/query";

import {
  type OpportunityAppointmentsQueryArgs,
  type SalesReviewsQueryArgs,
  useAnalyseOpportunityMutation,
  useGetOpportunityAppointmentsQuery,
  useGetSalesReviewQuery,
  useGetSalesReviewStatusQuery,
  useGetSalesReviewsQuery,
  useLazyGetSalesReviewStatusQuery,
  useMarkSalesReviewReadMutation
} from "services/sales-coach";
import {
  useAnalyseOpportunityV2Mutation,
  useGetOpportunityAppointmentsV2Query,
  useGetSalesReviewStatusV2Query,
  useGetSalesReviewV2Query,
  useGetSalesReviewsV2Query,
  useLazyGetSalesReviewStatusV2Query,
  useMarkSalesReviewReadV2Mutation,
  useReanalyseOpportunityV2Mutation
} from "services/sales-coach-v2";

import useSalesCoachVersion from "./sales-coach-version.hook";

/*
 * Every Sales Coach review and opportunity read goes through here, so v1 and v2 can never be
 * mixed on one screen (SN-19967).
 *
 * Both hooks are always called — the inactive one on `skipToken` — because which one is active
 * can change between renders, and a hook must not appear or disappear. The active result is
 * returned WHOLE: callers read `data` through a refetch, `currentData` for the current arg, and
 * `originalArgs` for the Training scope guard, so a narrowed result would quietly break one of
 * them. While the version is undefined both are skipped; the two roots that settle it
 * (`SalesCoachLayout`, `useSalesCoachReviewAccess`) never render a caller in that window.
 */

type OpportunitiesOptions = Parameters<
  typeof useGetOpportunityAppointmentsQuery
>[1];

export function useSalesCoachOpportunitiesQuery(
  args: OpportunityAppointmentsQueryArgs | typeof skipToken,
  options?: OpportunitiesOptions
) {
  const version = useSalesCoachVersion();
  const v1 = useGetOpportunityAppointmentsQuery(
    version === "v1" ? args : skipToken,
    options
  );
  const v2 = useGetOpportunityAppointmentsV2Query(
    version === "v2" ? args : skipToken,
    options
  );
  return version === "v2" ? v2 : v1;
}

type ReviewsOptions = Parameters<typeof useGetSalesReviewsQuery>[1];

export function useSalesCoachReviewsQuery(
  args: SalesReviewsQueryArgs | typeof skipToken,
  options?: ReviewsOptions
) {
  const version = useSalesCoachVersion();
  const v1 = useGetSalesReviewsQuery(
    version === "v1" ? args : skipToken,
    options
  );
  const v2 = useGetSalesReviewsV2Query(
    version === "v2" ? args : skipToken,
    options
  );
  return version === "v2" ? v2 : v1;
}

type ReviewOptions = Parameters<typeof useGetSalesReviewQuery>[1];

export function useSalesCoachReviewQuery(
  reviewId: number | typeof skipToken,
  options?: ReviewOptions
) {
  const version = useSalesCoachVersion();
  const v1 = useGetSalesReviewQuery(
    version === "v1" ? reviewId : skipToken,
    options
  );
  const v2 = useGetSalesReviewV2Query(
    version === "v2" ? reviewId : skipToken,
    options
  );
  return version === "v2" ? v2 : v1;
}

type StatusOptions = Parameters<typeof useGetSalesReviewStatusQuery>[1];

export function useSalesCoachReviewStatusQuery(
  reviewId: number | typeof skipToken,
  options?: StatusOptions
) {
  const version = useSalesCoachVersion();
  const v1 = useGetSalesReviewStatusQuery(
    version === "v1" ? reviewId : skipToken,
    options
  );
  const v2 = useGetSalesReviewStatusV2Query(
    version === "v2" ? reviewId : skipToken,
    options
  );
  return version === "v2" ? v2 : v1;
}

/** Both lazies are created; only the active one is ever triggered. */
export function useLazySalesCoachReviewStatusQuery() {
  const version = useSalesCoachVersion();
  const v1 = useLazyGetSalesReviewStatusQuery();
  const v2 = useLazyGetSalesReviewStatusV2Query();
  return version === "v2" ? v2 : v1;
}

export function useSalesCoachAnalyseMutation() {
  const version = useSalesCoachVersion();
  const v1 = useAnalyseOpportunityMutation();
  const v2 = useAnalyseOpportunityV2Mutation();
  return version === "v2" ? v2 : v1;
}

export function useSalesCoachMarkReadMutation() {
  const version = useSalesCoachVersion();
  const v1 = useMarkSalesReviewReadMutation();
  const v2 = useMarkSalesReviewReadV2Mutation();
  return version === "v2" ? v2 : v1;
}

/** v2 only (PRD D25): v1 has no re-analyse route, and only a v2 surface ever renders the control. */
export function useSalesCoachReanalyseMutation() {
  return useReanalyseOpportunityV2Mutation();
}
